import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';

export type AlertType = 'danger' | 'warning' | 'success' | 'info';

interface AlertModalProps {
    isOpen: boolean;
    toggle: () => void;
    title: string;
    message: string | React.ReactNode;
    type?: AlertType;
    icon?: string;
    confirmText?: string;
    onConfirm?: () => void;
    showCancel?: boolean;
    cancelText?: string;
}

const AlertModal: React.FC<AlertModalProps> = ({
    isOpen,
    toggle,
    title,
    message,
    type = 'danger',
    icon,
    confirmText = 'Cerrar',
    onConfirm,
    showCancel = false,
    cancelText = 'Cancelar'
}) => {
    const getTypeConfig = (alertType: AlertType) => {
        const configs = {
            danger: {
                headerClass: 'bg-danger text-white',
                iconBgColor: '#fff3e0',
                iconColor: 'text-danger',
                icon: icon || 'ri-error-warning-line',
                buttonColor: 'danger'
            },
            warning: {
                headerClass: 'bg-warning text-white',
                iconBgColor: '#fff8e1',
                iconColor: 'text-warning',
                icon: icon || 'ri-alarm-warning-line',
                buttonColor: 'warning'
            },
            success: {
                headerClass: 'bg-success text-white',
                iconBgColor: '#e8f5e9',
                iconColor: 'text-success',
                icon: icon || 'ri-checkbox-circle-line',
                buttonColor: 'success'
            },
            info: {
                headerClass: 'bg-info text-white',
                iconBgColor: '#e3f2fd',
                iconColor: 'text-info',
                icon: icon || 'ri-information-line',
                buttonColor: 'info'
            }
        };
        return configs[alertType];
    };

    const config = getTypeConfig(type);

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        toggle();
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="md"
            backdrop="static"
            fade
            className="border-0"
            modalClassName="modal-dialog-centered"
            contentClassName="border-0 rounded-4 shadow overflow-hidden"
        >
            <ModalHeader
                className={`p-3 ${config.headerClass}`}
            >
                <h5 className="modal-title mb-0 text-white">{title}</h5>
                <button
                    type="button"
                    onClick={toggle}
                    className="btn p-0 border-0 position-absolute top-0 end-0 m-3"
                    style={{ background: 'transparent' }}
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </ModalHeader>
            <ModalBody className="px-4 py-4 text-center">
                <div
                    className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                        width: '72px',
                        height: '72px',
                        backgroundColor: config.iconBgColor
                    }}
                >
                    <i className={`${config.icon} fs-1 ${config.iconColor}`}></i>
                </div>

                {typeof message === 'string' ? (
                    <p className="text-muted mb-0">{message}</p>
                ) : (
                    message
                )}
            </ModalBody>
            <ModalFooter className="px-4 py-3 border-top">
                <div className="w-100 d-flex justify-content-end gap-2">
                    {showCancel && (
                        <Button
                            color="light"
                            onClick={toggle}
                        >
                            {cancelText}
                        </Button>
                    )}
                    <Button
                        color={config.buttonColor}
                        onClick={handleConfirm}
                    >
                        {confirmText}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default AlertModal;
